import { useState } from 'react';
import { theme } from '../../theme.js';
import ChartCard from './ChartCard.jsx';

export default function BarList({ data = [] }) {
  const [sortConfig, setSortConfig] = useState({ key: 'race_name', direction: 'asc' });

  // Ensure data is an array
  const dataArray = Array.isArray(data) ? data : [];

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  // Calculate totals
  const totalAvailable = dataArray.reduce((sum, row) => sum + (row.total_tickets || 0), 0);
  const totalSold = dataArray.reduce((sum, row) => sum + (row.tickets_sold || 0), 0);
  const soldPercentage = totalAvailable > 0 ? (totalSold / totalAvailable) * 100 : 0;

  const sortedData = [...dataArray].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const columns = [
    { key: 'race_name', label: 'Race' },
    { key: 'price_name', label: 'Price' },
    { key: 'ticket_price', label: 'Price ($)' },
    { key: 'total_tickets', label: 'Total' },
    { key: 'tickets_sold', label: 'Sold' },
    { key: 'tickets_left', label: 'Left' },
  ];

  if (dataArray.length === 0) {
    return (
      <ChartCard>
        <div style={styles.container}>
          <p style={styles.emptyMessage}>No ticket data available</p>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>Tickets Overview</h3>
        </div>

        {/* Progress Bar */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${soldPercentage}%`,
                backgroundColor: theme.colors.light,
              }}
            />
            <div 
              style={{
                ...styles.progressFill,
                width: `${100 - soldPercentage}%`,
                backgroundColor: '#D1D5DB',
              }}
            />
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsContainer}>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Sold</div>
            <div style={styles.statValue}>{totalSold} <span style={styles.percent}>({soldPercentage.toFixed(1)}%)</span></div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Available</div>
            <div style={styles.statValue}>{totalAvailable - totalSold} <span style={styles.percent}>({(100 - soldPercentage).toFixed(1)}%)</span></div>
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={styles.headerCell}
                    onClick={() => handleSort(col.key)}
                  >
                    <div style={styles.headerContent}>
                      {col.label}
                      {sortConfig.key === col.key && (
                        <span style={styles.sortIcon}>
                          {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr key={`${row.race_id}-${row.price_id}`} style={{
                  ...styles.bodyRow,
                  backgroundColor: idx % 2 === 0 ? theme.colors.white : theme.colors.bgLight,
                }}>
                  <td style={styles.cell}>{row.race_name}</td>
                  <td style={styles.cell}>{row.price_name}</td>
                  <td style={styles.cell}>${row.ticket_price?.toFixed(2) || 'N/A'}</td>
                  <td style={styles.cell}>{row.total_tickets}</td>
                  <td style={styles.cell}>
                    <span style={{...styles.badge, backgroundColor: '#D1FAE5', color: '#065F46'}}>
                      {row.tickets_sold}
                    </span>
                  </td>
                  <td style={styles.cell}>
                    {row.tickets_left !== null ? (
                      <span style={{...styles.badge, backgroundColor: '#FEF3C7', color: '#92400E'}}>
                        {row.tickets_left}
                      </span>
                    ) : (
                      <span style={{...styles.badge, backgroundColor: '#F3F4F6', color: '#6B7280'}}>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <p style={styles.footerText}>Showing {sortedData.length} price entries out of {totalAvailable} total tickets</p>
        </div>
      </div>
    </ChartCard>
  );
}

const styles = {
  container: {
    width: '600px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
    margin: 0,
  },
  progressContainer: {
    marginBottom: theme.spacing.lg,
  },
  progressBar: {
    display: 'flex',
    height: '12px',
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
    backgroundColor: '#E5E7EB',
    marginBottom: theme.spacing.md,
  },
  progressFill: {
    transition: 'width 0.3s ease',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.lg,
  },
  statBox: {
    flex: 1,
  },
  statLabel: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    fontWeight: theme.fonts.weight.medium,
    marginBottom: theme.spacing.xs,
  },
  statValue: {
    fontSize: '20px',
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.textInverted,
  },
  percent: {
    fontSize: theme.fonts.size.md,
    color: theme.colors.textMuted,
    fontWeight: theme.fonts.weight.normal,
    marginLeft: theme.spacing.xs,
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: theme.spacing.lg,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: theme.colors.bgLight,
    borderBottom: `2px solid ${theme.colors.border}`,
  },
  headerCell: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    textAlign: 'left',
    fontWeight: theme.fonts.weight.bold,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.2s',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  sortIcon: {
    fontSize: '0.75rem',
    color: theme.colors.light,
  },
  bodyRow: {
    borderBottom: `1px solid ${theme.colors.border}`,
    transition: 'background-color 0.2s',
  },
  cell: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textInverted,
  },
  badge: {
    display: 'inline-block',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.radius.sm,
    fontSize: theme.fonts.size.xs,
    fontWeight: theme.fonts.weight.bold,
  },
  footer: {
    paddingTop: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.border}`,
  },
  footerText: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    margin: 0,
  },
  emptyMessage: {
    fontSize: theme.fonts.size.md,
    color: theme.colors.textMuted,
    textAlign: 'center',
    padding: theme.spacing.lg,
    margin: 0,
  },
};